import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft,
  Car,
  User,
  Calendar,
  MapPin,
  FileText,
  Image,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Phone,
  Mail,
  ExternalLink,
  Copy,
  Share2,
  Send,
  FileCheck,
  CreditCard,
  Loader2,
  Trash2,
  Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';

import { API_URL } from '../utils/api';

const statusConfig = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', icon: FileText },
  submitted: { label: 'Soumis', color: 'bg-blue-100 text-blue-700', icon: Clock },
  validated: { label: 'Validé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  tiers_contacted: { label: 'Tiers contacté', color: 'bg-amber-100 text-amber-700', icon: Clock },
  tiers_accepted: { label: 'Tiers accepté', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  tiers_refused: { label: 'Tiers refusé', color: 'bg-rose-100 text-rose-700', icon: AlertTriangle },
  garage_selected: { label: 'Garage sélectionné', color: 'bg-blue-100 text-blue-700', icon: Car },
  quote_received: { label: 'Devis reçu', color: 'bg-amber-100 text-amber-700', icon: FileText },
  quote_validated: { label: 'Devis validé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  signature_pending: { label: 'Signature en attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  signed: { label: 'Signé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  payment_pending: { label: 'Paiement en attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  payment_confirmed: { label: 'Paiement confirmé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  repair_in_progress: { label: 'Réparation en cours', color: 'bg-blue-100 text-blue-700', icon: Car },
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  cancelled: { label: 'Annulé', color: 'bg-slate-100 text-slate-700', icon: AlertTriangle },
  escalated_to_insurance: { label: 'Basculé assurance', color: 'bg-rose-100 text-rose-700', icon: AlertTriangle }
};

const ClaimDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchClaim = async () => {
    try {
      const response = await axios.get(`${API_URL}/claims/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaim(response.data);
    } catch (err) {
      setError('Erreur lors du chargement du dossier');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClaim();
  }, [id, token]);

  // Action handlers
  const handleValidateClaim = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API_URL}/claims/${id}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.email_sent 
        ? 'Dossier validé - Email envoyé au tiers' 
        : 'Dossier validé');
      fetchClaim();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleValidateQuote = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/claims/${id}/validate-quote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devis validé');
      fetchClaim();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la validation du devis');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/claims/${id}/confirm-payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paiement confirmé');
      fetchClaim();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la confirmation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseClaim = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/claims/${id}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dossier clôturé');
      fetchClaim();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la clôture');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClaim = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/claims/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dossier supprimé');
      setShowDeleteDialog(false);
      navigate('/claims');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const copyPortalLink = () => {
    const link = `${window.location.origin}/portal/third-party/${claim.third_party_portal_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Lien copié dans le presse-papier');
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="claim-detail-loading">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !claim) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <p className="text-slate-500">{error || 'Dossier non trouvé'}</p>
        <Link to="/claims">
          <Button variant="outline" className="mt-4">
            Retour aux dossiers
          </Button>
        </Link>
      </div>
    );
  }

  const config = statusConfig[claim.status] || { label: claim.status, color: 'bg-slate-100 text-slate-700' };
  const StatusIcon = config.icon || FileText;
  const unresolvedFlags = claim.risk_flags?.filter(f => !f.resolved) || [];

  return (
    <div className="space-y-6 pb-20 lg:pb-8" data-testid="claim-detail">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/claims">
            <Button variant="ghost" size="icon" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {claim.reference}
              </h1>
              <Badge variant="secondary" className={`${config.color}`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {config.label}
              </Badge>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Créé le {formatDate(claim.created_at)}
            </p>
          </div>
        </div>
        
        {claim.third_party_portal_token && isAdmin() && (
          <Button variant="outline" onClick={copyPortalLink} data-testid="copy-portal-link-btn">
            <Share2 className="w-4 h-4 mr-2" />
            Copier le lien tiers
          </Button>
        )}
      </div>

      {/* Actions Card - Visible for admins */}
      {isAdmin() && (
        <Card className="border-2 border-blue-200 bg-blue-50" data-testid="actions-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-blue-900">
              <FileCheck className="w-5 h-5" />
              Actions disponibles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {/* Note: L'email au tiers est envoyé automatiquement lors de la soumission par le chauffeur */}
              {/* Le bouton "Valider & Envoyer au tiers" a été supprimé car le workflow est maintenant automatique */}

              {/* Validate quote */}
              {claim.status === 'quote_received' && (
                <Button 
                  onClick={handleValidateQuote}
                  disabled={actionLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="validate-quote-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Valider le devis ({claim.quote_amount}€)
                </Button>
              )}

              {/* Confirm payment */}
              {claim.status === 'signed' && (
                <Button 
                  onClick={handleConfirmPayment}
                  disabled={actionLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-payment-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                  Confirmer le paiement
                </Button>
              )}

              {/* Close claim */}
              {claim.status === 'payment_confirmed' && (
                <Button 
                  onClick={handleCloseClaim}
                  disabled={actionLoading}
                  className="bg-slate-600 hover:bg-slate-700"
                  data-testid="close-claim-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Clôturer le dossier
                </Button>
              )}

              {/* No action available message */}
              {!['submitted', 'quote_received', 'signed', 'payment_confirmed'].includes(claim.status) && (
                <p className="text-sm text-blue-700 italic">
                  {claim.status === 'tiers_contacted' && "En attente de l'acceptation par le tiers"}
                  {claim.status === 'tiers_accepted' && "En attente du devis du tiers"}
                  {claim.status === 'quote_validated' && "En attente de la signature du tiers"}
                  {claim.status === 'signature_pending' && "En attente de la signature du tiers"}
                  {claim.status === 'completed' && "Dossier terminé"}
                  {claim.status === 'draft' && "Dossier en brouillon - en attente de soumission par le chauffeur"}
                </p>
              )}

              {/* Copy portal link */}
              {claim.third_party_portal_token && (
                <Button variant="outline" onClick={copyPortalLink} className="ml-auto">
                  <Copy className="w-4 h-4 mr-2" />
                  Copier lien portail
                </Button>
              )}

              {/* Delete claim button */}
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteDialog(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                data-testid="delete-claim-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Risk Flags */}
      {unresolvedFlags.length > 0 && (
        <Alert variant="destructive" data-testid="risk-flags-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Alertes actives</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 space-y-1">
              {unresolvedFlags.map((flag, i) => (
                <li key={i} className="text-sm">• {flag.description}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="w-full md:w-auto grid grid-cols-4 md:flex">
          <TabsTrigger value="details" data-testid="tab-details">Détails</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Historique</TabsTrigger>
          <TabsTrigger value="third-party" data-testid="tab-third-party">Tiers</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-6 mt-6">
          {/* Incident Info */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-slate-600" />
                Incident
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {claim.driver_name && (
                <div>
                  <p className="text-sm text-slate-500">Chauffeur de bus</p>
                  <p className="font-medium">{claim.driver_name}</p>
                </div>
              )}
              {claim.depot_manager_name && (
                <div>
                  <p className="text-sm text-slate-500">Chef de dépôt</p>
                  <p className="font-medium">{claim.depot_manager_name}</p>
                </div>
              )}
              {claim.constat_signed !== undefined && (
                <div>
                  <p className="text-sm text-slate-500">Constat rempli et signé</p>
                  <p className={`font-medium ${claim.constat_signed ? 'text-emerald-600' : 'text-red-600'}`}>
                    {claim.constat_signed ? '✓ Oui' : '✗ Non'}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-500">Date et heure</p>
                <p className="font-medium">{formatDate(claim.incident_date)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Description</p>
                <p className="font-medium">{claim.incident_description}</p>
              </div>
              {claim.location?.address && (
                <div>
                  <p className="text-sm text-slate-500">Lieu</p>
                  <p className="font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {claim.location.address}
                    {claim.location.postal_code && `, ${claim.location.postal_code}`}
                    {claim.location.city && ` ${claim.location.city}`}
                  </p>
                </div>
              )}
              {claim.driver_notes && (
                <div>
                  <p className="text-sm text-slate-500">Notes du chauffeur</p>
                  <p className="font-medium">{claim.driver_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vehicles */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Car className="w-5 h-5 text-slate-600" />
                  Véhicule entreprise
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="px-3 py-1.5 bg-slate-100 rounded-lg font-mono font-bold">
                      {claim.company_vehicle?.plate}
                    </div>
                  </div>
                  {claim.company_vehicle?.brand && (
                    <p className="text-slate-600">
                      {claim.company_vehicle.brand} {claim.company_vehicle.model}
                      {claim.company_vehicle.year && ` (${claim.company_vehicle.year})`}
                    </p>
                  )}
                  {claim.company_vehicle?.color && (
                    <p className="text-sm text-slate-500">Couleur: {claim.company_vehicle.color}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Car className="w-5 h-5 text-slate-600" />
                  Véhicule tiers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {claim.third_party?.vehicle?.plate ? (
                  <div className="space-y-3">
                    <div className="px-3 py-1.5 bg-slate-100 rounded-lg font-mono font-bold inline-block">
                      {claim.third_party.vehicle.plate}
                    </div>
                    {claim.third_party.vehicle.brand && (
                      <p className="text-slate-600">
                        {claim.third_party.vehicle.brand} {claim.third_party.vehicle.model}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500">Non renseigné</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-6">
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Image className="w-5 h-5 text-slate-600" />
                Documents ({claim.documents?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {claim.documents?.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {claim.documents.map((doc) => {
                    const isPDF = doc.content_type === 'application/pdf' || doc.filename?.toLowerCase().endsWith('.pdf');
                    const isImage = doc.content_type?.startsWith('image/');
                    const hasData = doc.data && doc.data.length > 0;
                    
                    return (
                      <div 
                        key={doc.id} 
                        className={`aspect-square rounded-xl overflow-hidden bg-slate-100 relative group cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all ${!hasData ? 'opacity-50' : ''}`}
                        data-testid={`document-${doc.id}`}
                        onClick={() => {
                          if (!hasData) {
                            toast.error('Document non disponible');
                            return;
                          }
                          // Open document in new tab
                          try {
                            const byteCharacters = atob(doc.data);
                            const byteNumbers = new Array(byteCharacters.length);
                            for (let i = 0; i < byteCharacters.length; i++) {
                              byteNumbers[i] = byteCharacters.charCodeAt(i);
                            }
                            const byteArray = new Uint8Array(byteNumbers);
                            const blob = new Blob([byteArray], { type: doc.content_type || 'application/octet-stream' });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          } catch (e) {
                            console.error('Error opening document:', e);
                            toast.error('Erreur lors de l\'ouverture du document');
                          }
                        }}
                      >
                        {isImage && hasData ? (
                          <img
                            src={`data:${doc.content_type};base64,${doc.data}`}
                            alt={doc.filename}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-4 bg-slate-50">
                            {isPDF ? (
                              <FileText className="w-12 h-12 text-red-500 mb-2" />
                            ) : (
                              <FileText className="w-12 h-12 text-slate-400 mb-2" />
                            )}
                            <p className="text-xs text-slate-600 text-center font-medium truncate w-full px-2">
                              {doc.filename || 'Document'}
                            </p>
                            {isPDF && (
                              <span className="text-xs text-red-500 mt-1">PDF</span>
                            )}
                            {!hasData && (
                              <span className="text-xs text-orange-500 mt-1">Non chargé</span>
                            )}
                          </div>
                        )}
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <div className="bg-white rounded-full p-2 shadow-lg">
                            <Eye className="w-5 h-5 text-slate-700" />
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                          <Badge variant="secondary" className="text-xs">
                            {doc.type === 'quote' ? 'Devis' : 
                             doc.type === 'garage_rib' ? 'RIB Garage' :
                             doc.type.replace('photo_', '').replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Image className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucun document</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="mt-6">
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-600" />
                Historique
              </CardTitle>
            </CardHeader>
            <CardContent>
              {claim.timeline?.length > 0 ? (
                <div className="space-y-4">
                  {claim.timeline.map((event, i) => (
                    <div key={i} className="flex gap-4" data-testid={`timeline-event-${i}`}>
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 bg-slate-300 rounded-full" />
                        {i < claim.timeline.length - 1 && (
                          <div className="w-px h-full bg-slate-200 my-1" />
                        )}
                      </div>
                      <div className="pb-4">
                        <p className="font-medium text-slate-900">{event.details}</p>
                        <p className="text-sm text-slate-500">{formatShortDate(event.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Aucun événement</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Third Party Tab */}
        <TabsContent value="third-party" className="mt-6">
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-slate-600" />
                Tiers identifié
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <p className="font-semibold text-lg">
                    {claim.third_party?.first_name} {claim.third_party?.last_name}
                  </p>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span>{claim.third_party?.phone}</span>
                </div>
                {claim.third_party?.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{claim.third_party.email}</span>
                  </div>
                )}
                {claim.third_party?.address && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{claim.third_party.address}</span>
                  </div>
                )}
              </div>

              {claim.third_party_portal_token && (
                <>
                  <Separator />
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-600 mb-3">
                      Lien d'accès au portail tiers:
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-white p-2 rounded border border-slate-200 truncate">
                        {window.location.origin}/portal/third-party/{claim.third_party_portal_token}
                      </code>
                      <Button size="sm" variant="outline" onClick={copyPortalLink}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Garage Info */}
          {claim.selected_garage && (
            <Card className="border-slate-100 mt-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  Garage sélectionné
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-semibold">{claim.selected_garage.name}</p>
                {claim.selected_garage.address && (
                  <p className="text-slate-600 mt-1">{claim.selected_garage.address}</p>
                )}
                {claim.selected_garage.phone && (
                  <p className="text-slate-600">{claim.selected_garage.phone}</p>
                )}
                {claim.quote_amount && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Montant du devis</p>
                    <p className="text-xl font-bold text-slate-900">{claim.quote_amount}€</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Supprimer le dossier
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>Êtes-vous sûr de vouloir supprimer le dossier <strong>{claim?.reference}</strong> ?</p>
              <p className="text-red-600 font-medium">Cette action est irréversible.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteDialog(false)}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleDeleteClaim} 
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-claim-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClaimDetail;
